/**
 * As duas decisões das etiquetas, sem React — a chave de comparação e o que o
 * Enter faz.
 *
 * Fora do componente porque as duas são regra e não desenho, e regra desta casa
 * se prende em teste (`etiquetas.test.jsx`). O defeito que elas existem para
 * impedir não aparece em print nenhum: o escritório digita "Urgente", "urgente"
 * e "URGENTE" na mesma semana, e sem dobrar acento e caixa o catálogo ganha três
 * linhas para um assunto — pior, o filtro da mesa passa a achar um terço dos
 * casos, cada um sob um rótulo diferente.
 */

/**
 * A chave de comparação de uma etiqueta: sem acento, sem caixa, sem borda.
 *
 * É ela que responde "esta etiqueta já existe?" e "este caso já tem esta?".
 */
export const chaveDaEtiqueta = (t) =>
  String(t ?? "")
    .normalize("NFD")
    /* Remove os diacríticos que o NFD separou da letra. */
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();

/**
 * O que o Enter no campo de etiqueta deve fazer.
 *
 * Três respostas, e a do meio é a que conserta o defeito: com "Urgente" no
 * catálogo, digitar "urgente" e apertar Enter tem de MARCAR a que existe, nunca
 * criar uma segunda. Criar a segunda é o começo de dois rótulos para o mesmo
 * assunto, e a partir daí nenhum filtro acha o caso inteiro.
 *
 * @param {string} texto o que foi digitado
 * @param {{tag: string}[]} catalogo as etiquetas do escritório
 * @param {string[]} noCaso as etiquetas que este caso já tem
 * @returns {{acao: "nada"|"marcar"|"criar", tag: string}}
 */
export function oQueEnterFaz(texto, catalogo = [], noCaso = []) {
  const chave = chaveDaEtiqueta(texto);
  /* Campo vazio (ou só espaço) não cria etiqueta sem nome. */
  if (!chave) return { acao: "nada", tag: "" };

  const existente = catalogo.find((c) => chaveDaEtiqueta(c?.tag) === chave);
  if (existente) {
    /* Já está no caso: Enter não desmarca. Quem tira usa o ✕ da etiqueta, que é
       um gesto deliberado — Enter num campo de busca tirando uma etiqueta do
       caso seria destruição por digitação. */
    const jaTem = noCaso.some((t) => chaveDaEtiqueta(t) === chave);
    return { acao: jaTem ? "nada" : "marcar", tag: existente.tag };
  }

  /* Nome novo: nasce escrito, com a caixa e o acento que a pessoa digitou — a
     dobra é só para COMPARAR, nunca para guardar. */
  return { acao: "criar", tag: String(texto).trim() };
}

/**
 * As cores escolhidas, prontas para consultar por etiqueta.
 *
 * A linha da mesa só tem os NOMES das etiquetas do caso (`caso.tags`, separado
 * por `|`); a cor é do rótulo e vive no catálogo do escritório
 * (`catalogoDeTags`). Um mapa por chave é o que liga os dois sem pedir nada ao
 * servidor — e é a mesma chave sem acento e sem caixa, senão "Urgência" pintada
 * não acharia a cor de "urgencia" escrita na linha.
 *
 * @param {{tag: string, cor?: string|null}[]} catalogo
 * @returns {Map<string, string|null>}
 */
export const coresDoCatalogo = (catalogo = []) => new Map((catalogo ?? []).map((c) => [chaveDaEtiqueta(c?.tag), c?.cor ?? null]));
